// src/features/streams/hooks/useFacebookSdk.ts

import { useState, useCallback, useRef, useEffect } from 'react';
import { FACEBOOK_LIVE_SCOPES } from '@/lib/constants/extplat';

type FBSdkStatus = 'idle' | 'loading' | 'ready' | 'error';

interface FBLoginResult {
  accessToken: string;
  expiresIn: number;
  userID: string;
}

interface UseFacebookSdkReturn {
  status: FBSdkStatus;
  login: () => Promise<FBLoginResult>;
  isReady: boolean;
}

// Module-level singleton promise prevents duplicate script injection
let sdkLoadPromise: Promise<void> | null = null;

function injectFacebookSdk(): Promise<void> {
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise<void>((resolve, reject) => {
    // If script is already present, just wait for FB to be available
    if (document.getElementById('facebook-jssdk')) {
      if (window.FB) {
        resolve();
        return;
      }
      // Fall through to polling
    } else {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    // Poll for window.FB readiness (200ms intervals, max 50 attempts = 10s timeout)
    let attempts = 0;
    const maxAttempts = 50;
    const interval = setInterval(() => {
      attempts++;
      if (window.FB) {
        clearInterval(interval);
        resolve();
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        sdkLoadPromise = null; // Allow retry
        reject(new Error('Facebook SDK failed to load within 10 seconds'));
      }
    }, 200);
  });

  return sdkLoadPromise;
}

/**
 * Feature-local hook for Facebook SDK lifecycle.
 * Lazy-loads the SDK only when triggered, with singleton prevention and timeout.
 */
export function useFacebookSdk(appId: string): UseFacebookSdkReturn {
  const [status, setStatus] = useState<FBSdkStatus>('idle');
  const initializedAppId = useRef<string | null>(null);

  // Initialize SDK when status transitions to loading
  useEffect(() => {
    if (status !== 'loading') return;

    let cancelled = false;

    (async () => {
      try {
        await injectFacebookSdk();
        if (cancelled) return;

        // Only call FB.init once per appId
        if (initializedAppId.current !== appId) {
          window.FB.init({
            appId,
            version: 'v21.0',
            cookie: true,
            xfbml: false,
          });
          initializedAppId.current = appId;
        }

        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [status, appId]);

  const login = useCallback((): Promise<FBLoginResult> => {
    if (status === 'idle') {
      setStatus('loading');
    }

    return new Promise<FBLoginResult>((resolve, reject) => {
      const attemptLogin = () => {
        if (!window.FB) {
          reject(new Error('Facebook SDK is not loaded'));
          return;
        }

        window.FB.login(
          (response: fb.StatusResponse) => {
            if (response.authResponse) {
              const auth = response.authResponse;
              resolve({
                accessToken: auth.accessToken ?? '',
                expiresIn: auth.expiresIn ?? 0,
                userID: auth.userID ?? '',
              });
            } else {
              reject(new Error('Facebook login was cancelled or failed'));
            }
          },
          {
            scope: FACEBOOK_LIVE_SCOPES.join(','),
            return_scopes: true,
            enable_profile_selector: true,
          },
        );
      };

      // If SDK is ready, login immediately
      if (status === 'ready' && window.FB) {
        attemptLogin();
        return;
      }

      // Otherwise wait for SDK to be ready, then login
      const waitForSdk = async () => {
        try {
          await injectFacebookSdk();

          if (initializedAppId.current !== appId) {
            window.FB.init({
              appId,
              version: 'v21.0',
              cookie: true,
              xfbml: false,
            });
            initializedAppId.current = appId;
          }

          setStatus('ready');
          attemptLogin();
        } catch (err) {
          setStatus('error');
          reject(err instanceof Error ? err : new Error('Failed to load Facebook SDK'));
        }
      };

      waitForSdk();
    });
  }, [status, appId]);

  return {
    status,
    login,
    isReady: status === 'ready',
  };
}
